import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject
} from 'react';
import { useDeviceDetect } from 'react-easyrocket';
import { useLocation } from 'react-router';
import { CARD_GAME_ROUTE, CATCH_GAME_ROUTE } from '../config/event/eventConfig';

function fullScreen(element: HTMLDivElement | null) {
  // @ts-ignore
  if (element?.requestFullscreen) {
    // @ts-ignore
    element.requestFullscreen();
    // @ts-ignore
  } else if (element?.webkitrequestFullscreen) {
    // @ts-ignore
    element.webkitRequestFullscreen();
    // @ts-ignore
  } else if (element?.mozRequestFullscreen) {
    // @ts-ignore
    element.mozRequestFullScreen();
  }
}

function fullScreenCancel() {
  const exitFn =
    document.exitFullscreen ||
    // @ts-ignore
    document.webkitExitFullscreen ||
    // @ts-ignore
    document.mozExitFullscreen;

  if (exitFn) {
    exitFn().catch(() => {});
  }
}

export const FullScreenContext = createContext<{
  onFullscreenClose: () => void;
  isFullscreen: boolean;
  refFullscreen: RefObject<HTMLDivElement> | undefined;
  refGameFullscreen: RefObject<HTMLDivElement> | undefined;
  onFullscreenOpen: () => void;
  isHideHUD: boolean;
  isFullscreenGameBoard: boolean;
  refGameBoardFullscreen: RefObject<HTMLDivElement> | undefined;
}>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFullscreenClose: () => {},
  isFullscreen: false,
  refFullscreen: undefined,
  refGameFullscreen: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFullscreenOpen: () => {},
  isHideHUD: false,
  isFullscreenGameBoard: false,
  refGameBoardFullscreen: undefined
});

export const useInitFullScreen = () => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const refFullscreen = useRef<HTMLDivElement>(null);
  const refGameFullscreen = useRef<HTMLDivElement>(null);
  const [isFullscreenGameBoard, setIsFullscreenGameBoard] = useState<boolean>(false);

  const refGameBoardFullscreen = useRef<HTMLDivElement>(null);

  const [isHideHUD, setIsHideHUD] = useState<boolean>(false);
  const { isIos } = useDeviceDetect();
  const { pathname } = useLocation();

  const onFullscreenClose = useCallback(() => {
    setIsHideHUD(false);
    fullScreenCancel();
    document.body.style.overflow = 'visible';
  }, []);

  const onFullscreenOpen = useCallback(() => {
    setIsHideHUD(true);
    fullScreen(refFullscreen.current);
    fullScreen(refGameBoardFullscreen.current);
    fullScreen(refGameFullscreen.current);
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    function onFullscreenChange() {
      const element = Boolean(document.fullscreenElement);
      setIsFullscreen(element);
      setIsFullscreenGameBoard(element);
      setIsHideHUD(element);

      element
        ? (document.body.style.overflow = 'hidden')
        : (document.body.style.overflow = 'visible');
    }

    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // FALLBACK QUIT FOR IOS FAKE FULL SCREEN MODE
  useEffect(() => {
    if (isIos && pathname !== CATCH_GAME_ROUTE && pathname !== CARD_GAME_ROUTE) {
      setIsHideHUD(false);
      document.body.style.overflow = 'visible';
    }
  }, [isIos, pathname]);

  return {
    onFullscreenClose,
    isFullscreen,
    refFullscreen,
    onFullscreenOpen,
    refGameFullscreen,
    isHideHUD,
    isFullscreenGameBoard,
    refGameBoardFullscreen
  };
};

export const FullScreenProvider = ({ children }: PropsWithChildren) => {
  const values = useInitFullScreen();

  return <FullScreenContext.Provider value={values}>{children}</FullScreenContext.Provider>;
};

export const useFullScreen = () => {
  return useContext(FullScreenContext);
};
